export default function HeroSection() {
  const handleScroll = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-[620px] md:min-h-[700px] flex items-center overflow-hidden"
    >
      {/* Background image with overlay */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Vibrant%20anime%20style%20illustration%20with%20dynamic%20color%20splashes%20in%20warm%20orange%20and%20coral%20tones%2C%20abstract%20geometric%20shapes%2C%20soft%20gradient%20background%20with%20subtle%20Japanese%20manga%20aesthetic%20elements%2C%20clean%20modern%20composition%2C%20energetic%20yet%20refined%20atmosphere%2C%20digital%20art%20style%20with%20smooth%20textures%20and%20flowing%20lines&width=1800&height=900&seq=hero-bg-anime-01&orientation=landscape"
          alt="51动漫背景"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-4 md:px-6 py-20 md:py-16">
        <div className="max-w-[1400px] mx-auto flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          {/* Left: Text */}
          <div className="flex-1 w-full text-center lg:text-left">
            {/* Tag */}
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-medium bg-accent-500/90 text-background-50 mb-5 whitespace-nowrap">
              官方正版平台 · 免费全集动漫
            </span>

            {/* H1 */}
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-[3.4rem] font-bold text-background-50 leading-tight mb-4">
              51动漫 · 免费日漫韩漫国漫
              <br className="hidden sm:block" />
              全集下拉式在线阅读
            </h1>

            {/* Slogan */}
            <p className="text-lg md:text-xl text-background-50/85 font-medium mb-6">
              收藏追更，你的专属动漫乐园
            </p>

            {/* Intro */}
            <p className="text-sm md:text-base text-background-50/70 max-w-[640px] mx-auto lg:mx-0 leading-relaxed mb-7">
              51动漫汇聚海量日漫、韩漫、国漫正版资源，支持全集下拉式连续阅读，无需翻页等待。
              每日同步更新热门新番与经典番剧，涵盖热血、恋爱、奇幻、搞笑等全品类，
              无论你是追新党还是补番党，这里都能一键直达你想看的每一话。
            </p>

            {/* Stats */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 md:gap-10 mb-8">
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-accent-400">20000+</div>
                <div className="text-xs md:text-sm text-background-50/60 mt-1">动漫资源</div>
              </div>
              <div className="w-px h-10 bg-background-50/20 hidden sm:block"></div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-accent-400">每日更新</div>
                <div className="text-xs md:text-sm text-background-50/60 mt-1">同步连载</div>
              </div>
              <div className="w-px h-10 bg-background-50/20 hidden sm:block"></div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-accent-400">全品类</div>
                <div className="text-xs md:text-sm text-background-50/60 mt-1">日韩国漫</div>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3">
              <a
                href="#index"
                onClick={(e) => handleScroll(e, '#index')}
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-lg transition-colors text-sm whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-circle-fill text-lg"></i>
                立即开始追番
              </a>
              <a
                href="#updates"
                onClick={(e) => handleScroll(e, '#updates')}
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-background-50/15 hover:bg-background-50/25 text-background-50 border border-background-50/30 font-semibold rounded-lg transition-colors text-sm whitespace-nowrap cursor-pointer"
              >
                <i className="ri-fire-line text-lg"></i>
                查看今日更新
              </a>
            </div>
          </div>

          {/* Right: Visual */}
          <div className="flex-1 w-full flex justify-center lg:justify-end">
            <div className="relative w-[280px] h-[380px] sm:w-[340px] sm:h-[460px] md:w-[400px] md:h-[540px] rounded-2xl overflow-hidden border-2 border-background-50/20">
              <img
                src="https://readdy.ai/api/search-image?query=Dynamic%20anime%20style%20illustration%20of%20multiple%20colorful%20manga%20characters%20in%20action%20poses%2C%20vibrant%20warm%20orange%20and%20coral%20color%20palette%2C%20modern%20Japanese%20anime%20art%20style%2C%20clean%20background%20with%20soft%20bokeh%20effect%2C%20energetic%20composition%20with%20dynamic%20angles%2C%20digital%20illustration%20with%20smooth%20shading%2C%20eye-catching%20visual%20for%20anime%20streaming%20platform&width=800&height=1080&seq=hero-visual-anime-01&orientation=portrait"
                alt="51动漫 - 免费日漫韩漫国漫在线阅读平台"
                className="w-full h-full object-cover object-top"
              />
              {/* Decorative badge */}
              <div className="absolute top-4 right-4 px-3 py-1.5 bg-primary-500 text-background-50 text-xs font-bold rounded-full whitespace-nowrap">
                HOT
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}